import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import DesignStudio from "@/components/DesignStudio";
import QRPreview from "@/components/QRPreview";
import { DEFAULT_DESIGN, buildEncodedData, buildVCardString } from "@/lib/qr";
import { getRawBlob } from "@/lib/download";
import api, { apiError } from "@/lib/api";

const FIELDS = ["prefix", "first_name", "last_name", "job_title", "company", "mobile", "email", "website", "instagram", "linkedin", "whatsapp"];
const TEMPLATE = "prefix,first_name,last_name,job_title,company,mobile,email,website,instagram,linkedin,whatsapp\nMr.,John,Smith,Product Lead,QR Code Creator Studio,+15552001234,john@qrcodecreatorstudio.com,https://qrcodecreatorstudio.com,,,\nMs.,Aria,Kapoor,Designer,QR Code Creator Studio,+15552009876,aria@qrcodecreatorstudio.com,,,,";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(",");
    const row = {};
    headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
    return row;
  });
}

export default function BulkCreator() {
  const [rows, setRows] = useState([]);
  const [bulkOff, setBulkOff] = useState(false);
  const [design, setDesign] = useState({ ...DEFAULT_DESIGN });
  const [progress, setProgress] = useState(null);
  const [created, setCreated] = useState([]);
  const [watermark, setWatermark] = useState(false);
  useEffect(() => { api.get("/company/plan").then(({ data }) => setWatermark(!!data.entitlements?.watermark)).catch(() => {}); api.get("/company/features").then(({ data }) => setBulkOff(data.features.bulk_creation === false)).catch(() => {}); }, []);

  const loadText = (text) => {
    const parsed = parseCSV(text).map((r) => ({ ...r, _valid: !!(r.first_name && (r.mobile || r.email)) }));
    setRows(parsed);
    setCreated([]);
    toast.success(`${parsed.length} rows parsed`);
  };

  const downloadTemplate = () => { const blob = new Blob([TEMPLATE], { type: "text/csv" }); saveAs(blob, "qr-code-creator-studio-employees-template.csv"); };

  const publishAll = async () => {
    const valid = rows.filter((r) => r._valid);
    if (!valid.length) return toast.error("No valid rows to publish");
    setProgress({ done: 0, total: valid.length, errors: [] });
    const out = [];
    for (let i = 0; i < valid.length; i++) {
      const r = valid[i];
      try {
        const data = {}; FIELDS.forEach((f) => { if (r[f]) data[f] = r[f]; });
        const { data: q } = await api.post("/qr", { name: `${data.first_name} ${data.last_name || ""}`.trim(), destination_type: "vcard", mode: "dynamic", status: "active", data, design, vcard_mode: "updateable", source: "bulk" });
        out.push({ ...q, data });
      } catch (e) { setProgress((p) => ({ ...p, errors: [...p.errors, `Row ${i + 1}: ${apiError(e.response?.data?.detail)}`] })); }
      setProgress((p) => ({ ...p, done: i + 1 }));
    }
    setCreated(out);
    toast.success(`Published ${out.length} employee QR codes`);
  };

  const exportZip = async () => {
    if (!created.length) return toast.error("Publish employees first");
    const zip = new JSZip();
    for (const q of created) {
      const encoded = buildEncodedData({ type: "vcard", mode: "dynamic", data: q.data, publicId: q.public_id, vcardMode: "updateable", dynamicUrl: q.dynamic_url });
      const png = await getRawBlob(design, encoded, "png", watermark);
      const safe = (q.name || q.public_id).replace(/[^a-z0-9]+/gi, "_");
      zip.file(`${safe}.png`, png);
      zip.file(`${safe}.vcf`, buildVCardString(q.data, true));
    }
    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, "qr-code-creator-studio-employee-qrcodes.zip");
    toast.success("ZIP exported");
  };

  if (bulkOff) return (<AppLayout><div data-testid="bulk-unavailable" className="rounded-2xl border border-border bg-card p-8 text-center"><h1 className="text-lg font-bold">Bulk creation isn't available</h1><p className="text-sm text-muted-foreground mt-1">Bulk QR creation is not included for this workspace. You can still create QR codes one at a time.</p></div></AppLayout>);
  return (
    <AppLayout>
      <h1 className="text-2xl font-display font-extrabold mb-1">Bulk employee QR creator</h1>
      <p className="text-sm text-muted-foreground mb-6">Import employees by CSV, apply one company design, publish, and export individual QR + vCard files as a ZIP. Unlimited employees — no cap.</p>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap gap-2 mb-4">
              <button data-testid="download-template" onClick={downloadTemplate} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent flex items-center gap-1.5"><Icon name="FileDown" className="h-4 w-4" />Download template</button>
              <label data-testid="upload-csv" className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent flex items-center gap-1.5 cursor-pointer"><Icon name="Upload" className="h-4 w-4" />Upload CSV<input type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { const rd = new FileReader(); rd.onload = () => loadText(rd.result); rd.readAsText(f); } }} /></label>
            </div>
            <textarea data-testid="csv-paste" placeholder="...or paste CSV rows here" onChange={(e) => e.target.value && loadText(e.target.value)} rows={4} className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm font-mono" />
          </div>

          {rows.length > 0 && (
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="p-4 flex items-center justify-between border-b border-border"><span className="font-display font-bold text-sm">{rows.length} rows · {rows.filter((r) => r._valid).length} valid</span></div>
              <div className="overflow-x-auto max-h-80">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground"><tr><th className="text-left p-2"></th><th className="text-left p-2">Name</th><th className="text-left p-2">Title</th><th className="text-left p-2">Mobile</th><th className="text-left p-2">Email</th></tr></thead>
                  <tbody>{rows.map((r, i) => (<tr key={i} data-testid={`bulk-row-${i}`} className="border-t border-border"><td className="p-2">{r._valid ? <Icon name="CheckCircle2" className="h-4 w-4 text-emerald-500" /> : <Icon name="AlertCircle" className="h-4 w-4 text-destructive" title="Needs name + phone/email" />}</td><td className="p-2">{[r.prefix, r.first_name, r.last_name].filter(Boolean).join(" ")}</td><td className="p-2 text-muted-foreground">{r.job_title}</td><td className="p-2 text-muted-foreground">{r.mobile}</td><td className="p-2 text-muted-foreground">{r.email}</td></tr>))}</tbody>
                </table>
              </div>
              <div className="p-4 border-t border-border flex flex-wrap gap-2">
                <button data-testid="bulk-publish" onClick={publishAll} className="rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:opacity-90 flex items-center gap-1.5"><Icon name="Rocket" className="h-4 w-4" />Publish all valid</button>
                <button data-testid="bulk-export-zip" onClick={exportZip} disabled={!created.length} className="rounded-xl border border-border px-5 py-2.5 text-sm font-semibold hover:bg-accent disabled:opacity-50 flex items-center gap-1.5"><Icon name="FileArchive" className="h-4 w-4" />Export ZIP ({created.length})</button>
              </div>
            </div>
          )}

          {progress && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center justify-between text-sm mb-2"><span className="font-semibold">Publishing {progress.done}/{progress.total}</span><span className="text-muted-foreground">{Math.round((progress.done / progress.total) * 100)}%</span></div>
              <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
              {progress.errors.length > 0 && <ul className="mt-3 space-y-1">{progress.errors.map((e, i) => <li key={i} className="text-xs text-destructive">{e}</li>)}</ul>}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 h-fit sticky top-20">
          <h3 className="font-display font-bold text-sm mb-3">Company design (applied to all)</h3>
          <div className="rounded-xl bg-white p-3 grid place-items-center mb-4"><div style={{ width: 180 }}><QRPreview design={design} data="https://qrcodecreatorstudio.com" /></div></div>
          <DesignStudio design={design} onChange={setDesign} />
        </div>
      </div>
    </AppLayout>
  );
}
